import { ENDPOINTS } from 'shared/constants/ENDPOINTS';
import { useAxios } from 'shared/hooks/useAxios';
import {
  IProgramSetupCreatePayload,
  IProgramSetupRequestPayload,
  IProgramSetupResponsePayload,
  IProgramSetupUpdatePayload,
} from 'shared/interfaces/IProgramSetup';
import { transformPayload } from 'shared/utils/transformPayload';

const { GET, POST, DELETE } = useAxios();

export interface ProgramSetupDaoPayload<T> {
  data: T;
  image: {
    youtube_image?: File;
    facebook_image?: File;
    recording_image?: File;
  };
}

function transformToFormData<T>(payload: ProgramSetupDaoPayload<T>) {
  const transformedPayload = transformPayload<T>(payload.data);

  const formData = new FormData();
  formData.append('token', transformedPayload);

  if (payload.image?.youtube_image) {
    formData.append('youtube_image', payload?.image?.youtube_image);
  }
  if (payload.image?.facebook_image) {
    formData.append('facebook_image', payload?.image?.facebook_image);
  }
  if (payload.image?.recording_image) {
    formData.append('recording_image', payload?.image?.recording_image);
  }

  return formData;
}

export const useProgramSetupDao = () => {
  const getProgramSetup = async (payload?: IProgramSetupRequestPayload) => {
    const transformedPayload = transformPayload<IProgramSetupRequestPayload>(payload);

    const response = await GET<IProgramSetupResponsePayload>({
      url: ENDPOINTS.PROGRAM_SETUP,
      params: {
        token: transformedPayload,
      },
    });

    return response.data;
  };

  const createProgramSetup = async (payload: ProgramSetupDaoPayload<IProgramSetupCreatePayload>) => {
    const formData = transformToFormData<IProgramSetupCreatePayload>(payload);

    const response = await POST({
      url: `${ENDPOINTS.PROGRAM_SETUP}/create`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  };

  const updateProgramSetup = async (payload: ProgramSetupDaoPayload<IProgramSetupUpdatePayload>) => {
    const formData = transformToFormData<IProgramSetupUpdatePayload>(payload);

    const response = await POST({
      url: `${ENDPOINTS.PROGRAM_SETUP}/update`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  };

  const deleteProgramSetup = async (id: string) => {
    const transformedPayload = transformPayload({ _id: id });

    const response = await DELETE({
      url: `${ENDPOINTS.PROGRAM_SETUP}/delete`,
      params: {
        token: transformedPayload,
      },
    });

    return response.data;
  };

  return {
    getProgramSetup,
    createProgramSetup,
    updateProgramSetup,
    deleteProgramSetup,
  };
};
